<template>
  <div class="question-display">
    <div class="question-view">
      <div class="question-content">
        <!-- Fragen-Header mit Nummer und Status -->
        <div class="question-header">
          <span class="question-badge bg-primary">{{ index + 1 }}/{{ totalQuestions }}</span>
          <span v-if="isReviewMode && question.type === 'multiple_choice'" class="question-badge" :class="isCorrect ? 'bg-success' : 'bg-danger'">
            <i class="fas" :class="isCorrect ? 'fa-check' : 'fa-times'"></i>
            {{ isCorrect ? 'Korrekt' : 'Falsch' }}
          </span>
        </div>
        
        <!-- Bild/Medien wenn vorhanden -->
        <div v-if="question.image_url" class="question-media">
          <img :src="question.image_url" :alt="`Bild für Frage ${index + 1}`">
        </div>
        
        <!-- Fragen-Text -->
        <div class="question-text" v-html="question.description"></div>
        
        <!-- Multiple Choice Optionen -->
        <div v-if="question.type === 'multiple_choice'" class="answer-options">
          <label 
            v-for="(option, optIndex) in getQuestionOptions(question)" 
            :key="`option_${optIndex}`"
            class="option-item"
            :class="{ 
              'option-selected': selectedOptions.includes(optIndex + 1) && !isReviewMode,
              'option-correct': isReviewMode && isCorrectOption(question, optIndex + 1),
              'option-incorrect': isReviewMode && selectedOptions.includes(optIndex + 1) && !isCorrectOption(question, optIndex + 1)
            }"
            :for="`option_${questionId}_${optIndex}`"
          >
            <div class="form-check">
              <input 
                :type="question.allow_multiple_answers ? 'checkbox' : 'radio'" 
                class="form-check-input" 
                :id="`option_${questionId}_${optIndex}`"
                :name="`question_${questionId}`"
                :value="optIndex + 1"
                :checked="selectedOptions.includes(optIndex + 1)"
                :disabled="isReviewMode"
                @change="handleOptionChange($event, optIndex + 1)"
              >
              <span class="form-check-label">{{ option }}</span>
            </div>
            
            <!-- Icon für korrekte und falsche Antworten -->
            <div v-if="isReviewMode" class="option-icon">
              <!-- Häkchen für korrekte Optionen -->
              <i v-if="isCorrectOption(question, optIndex + 1)" 
                 class="fas fa-check-circle correct"></i>
              
              <!-- X für falsch ausgewählte Optionen -->
              <i v-else-if="selectedOptions.includes(optIndex + 1) && !isCorrectOption(question, optIndex + 1)" 
                 class="fas fa-times-circle incorrect"></i>
            </div>
          </label>
        </div>
        
        <!-- Offene Frage (Textfeld) -->
        <div v-else class="text-answer">
          <textarea 
            :id="`text-answer-${questionId}`"
            v-model="textAnswer"
            placeholder="Geben Sie Ihre Antwort hier ein..."
            :readonly="isReviewMode"
            @input="handleTextInput"
          ></textarea>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuestionDisplay',
  props: {
    question: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    totalQuestions: {
      type: Number,
      required: true
    },
    answer: {
      type: [Array, String, Object],
      default: () => null
    },
    isReviewMode: {
      type: Boolean,
      default: false
    },
    userAnswers: {
      type: Array,
      default: () => []
    },
    textAnswer: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectedOptions: [],
      textAnswer: ''
    };
  },
  computed: {
    questionId() {
      return this.question.id || this.index;
    },
    isCorrect() {
      if (!this.isReviewMode) return false;
      
      // Debug-Ausgabe
      console.log(`Überprüfe Frage ${this.questionId}:`, this.question);
      console.log(`Ausgewählte Optionen:`, this.selectedOptions);
      
      // KRITISCH: Wenn das Backend bereits eine Punktzahl vergeben hat, nutze AUSSCHLIESSLICH diese Information
      if (this.answer && typeof this.answer === 'object') {
        // Wenn die Antwort vom Backend kommt und points_earned > 0 ist, dann war die Antwort korrekt
        if (this.answer.points_earned > 0) {
          console.log(`Frage als KORREKT markiert durch Backend-Punkte: ${this.answer.points_earned}`);
          return true;
        }
        
        // Wenn vom Backend eindeutig als falsch markiert (points_earned = 0 und max_points > 0)
        if ('points_earned' in this.answer && 
             this.answer.points_earned === 0 && 
             this.answer.max_points > 0 &&
             // Zusätzliche Bedingung: Nur wenn auch eine Auswahl getroffen wurde
             ((Array.isArray(this.selectedOptions) && this.selectedOptions.length > 0 && 
               !this.selectedOptions.includes(0)) || 
              (this.textAnswer && this.textAnswer.trim() !== ''))) {
          
          console.log('Frage als FALSCH markiert durch Backend-Punkte: 0');
          return false;
        }
      }
      
      // Wenn keine klare Backend-Entscheidung vorliegt, 
      // aber die ausgewählten Optionen mit den korrekten übereinstimmen
      if (this.question.type === 'multiple_choice') {
        // Alle korrekten Optionen identifizieren
        const correctOptions = [];
        const selectedNonZeroOptions = this.selectedOptions.filter(opt => opt !== 0);
        
        // Prüfe alle möglichen Feldnamen für korrekte Antworten
        for (let i = 1; i <= 6; i++) {
          if (this.isCorrectOption(this.question, i)) {
            correctOptions.push(i);
          }
        }
        
        console.log(`Korrekte Optionen:`, correctOptions);
        console.log(`Ausgewählte Optionen (ohne 0):`, selectedNonZeroOptions);
        
        // Bei Single-Choice
        if (!this.question.allow_multiple_answers) {
          if (selectedNonZeroOptions.length === 1 && correctOptions.includes(selectedNonZeroOptions[0])) {
            console.log('Single-Choice: KORREKT');
            return true;
          }
        } 
        // Bei Multiple-Choice
        else {
          // Richtiger Fall 1: Alle korrekten wurden ausgewählt und keine falschen
          if (correctOptions.length > 0 && 
              selectedNonZeroOptions.length === correctOptions.length &&
              correctOptions.every(opt => selectedNonZeroOptions.includes(opt)) && 
              selectedNonZeroOptions.every(opt => correctOptions.includes(opt))) {
            return true;
          }
        }
        
        // Als letztes Mittel: Wenn wir Punktinformationen haben und sie positiv sind
        if (this.answer && typeof this.answer === 'object' && 'points_earned' in this.answer) {
          const isCorrect = this.answer.points_earned > 0;
          return isCorrect;
        }
      }
      
      // Bei Freitext-Fragen
      if (this.question.type !== 'multiple_choice') {
        if (this.textAnswer && this.textAnswer.trim() !== '') {
          // Freitext-Antworten gelten als korrekt, wenn eine Antwort eingegeben wurde
          return true;
        }
      }
      
      // Standardmäßig falsch, wenn keine der Bedingungen zutrifft
      console.log('FINAL: Als falsch bewertet (Standard)');
      return false;
    }
  },
  watch: {
    // Aktualisieren, wenn sich die Frage ändert
    question: {
      immediate: true,
      handler() {
        this.resetAnswers();
        this.loadAnswer();
      }
    },
    // Aktualisieren, wenn sich die Antwort von außen ändert
    answer: {
      handler() {
        this.loadAnswer();
      },
      deep: true
    },
    userAnswers: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.selectedOptions = [...newVal];
        }
      }
    },
    textAnswer: {
      immediate: true,
      handler(newVal) {
        if (newVal !== undefined) {
          this.textAnswer = newVal;
        }
      }
    }
  },
  methods: {
    resetAnswers() {
      this.selectedOptions = [];
      this.textAnswer = '';
    },
    loadAnswer() {
      const currentAnswer = this.answer;
      
      console.log(`loadAnswer für Frage ${this.questionId}:`, currentAnswer);
      
      if (!currentAnswer) {
        this.resetAnswers();
        return;
      }
      
      // Bei Antworten vom Backend können wir die Korrektheit direkt prüfen
      const isFromBackend = currentAnswer && typeof currentAnswer === 'object' && 
                          ('points_earned' in currentAnswer || 'selected_options' in currentAnswer);
      
      if (isFromBackend) {
        console.log('Antwort vom Backend erkannt');
      }
      
      if (this.question.type === 'multiple_choice') {
        // Mehrere Möglichkeiten für das Format der Antwort prüfen
        if (Array.isArray(currentAnswer)) {
          // Direkt ein Array von gewählten Optionen
          console.log('Array-Format erkannt:', currentAnswer);
          this.selectedOptions = [...currentAnswer];
        } else if (currentAnswer.selected_options && Array.isArray(currentAnswer.selected_options)) {
          // Object mit selected_options-Eigenschaft
          console.log('selected_options erkannt:', currentAnswer.selected_options);
          this.selectedOptions = [...currentAnswer.selected_options];
        } else if (typeof currentAnswer === 'object') {
          // Objekt mit answer_X_selected Eigenschaften
          console.log('Objekt mit answer_X_selected erkannt');
          const selected = [];
          for (let i = 1; i <= 6; i++) {
            if (currentAnswer[`answer_${i}_selected`] === true) {
              selected.push(i);
              console.log(`Option ${i} ist ausgewählt`);
            }
          }
          this.selectedOptions = selected.length > 0 ? selected : [];
        }
        
        console.log('Finale ausgewählte Optionen:', this.selectedOptions);
      } else {
        // Freitextantwort
        if (typeof currentAnswer === 'string') {
          this.textAnswer = currentAnswer;
        } else if (currentAnswer.text_answer) {
          this.textAnswer = currentAnswer.text_answer;
        } else if (currentAnswer.open_answer) {
          this.textAnswer = currentAnswer.open_answer;
        }
      }
    },
    getQuestionOptions(question) {
      const options = [];
      
      if (!question) return options;
      
      // Für beide mögliche Feldnamensformate prüfen
      for (let i = 1; i <= 6; i++) {
        const option = question[`answer_${i}`];
        
        // Nur nicht-leere Optionen hinzufügen
        if (option && option.trim() !== '') {
          options.push(option);
        }
      }
      
      return options;
    },
    isCorrectOption(question, optionIndex) {
      // Wenn vom Backend ein vollständiges Frage-Objekt kommt, sollten wir das verwenden
      const questionToCheck = question.question || question;
      
      // Alle möglichen Feldnamen überprüfen
      // Zuerst explizite Prüfung auf jeden möglichen Feldnamen
      const isCorrectA = questionToCheck[`correct_${optionIndex}`] === true;
      const isCorrectB = questionToCheck[`is_answer_${optionIndex}_correct`] === true;
      const isCorrectC = questionToCheck[`is_correct_${optionIndex}`] === true;
      const isCorrectD = questionToCheck[`correct${optionIndex}`] === true;
      const isCorrectE = questionToCheck[`isAnswer${optionIndex}Correct`] === true;
      const isCorrectF = questionToCheck[`isCorrect${optionIndex}`] === true;
      
      // Oder-Verknüpfung aller Ergebnisse
      const isCorrect = isCorrectA || isCorrectB || isCorrectC || isCorrectD || isCorrectE || isCorrectF;
      
      // Debug-Ausgabe für jeden Feldnamen (hilfreich bei der Fehlersuche)
      console.log(`Option ${optionIndex} Prüfung:`, 
                  `A=${isCorrectA}`, 
                  `B=${isCorrectB}`, 
                  `C=${isCorrectC}`, 
                  `D=${isCorrectD}`, 
                  `E=${isCorrectE}`, 
                  `F=${isCorrectF}`, 
                  `=> ${isCorrect}`);
      
      // Als Notfallsicherung: Wenn die Antwort vom Backend kommt und die entsprechende answer_X_selected auf true gesetzt ist,
      // aber keine der korrekten Felder auf true ist, nehmen wir an, dass es eine richtige Antwort ist
      if (!isCorrect && this.answer && typeof this.answer === 'object' && 
          (this.answer.points_earned > 0 || this.isCorrect) && 
          ((this.answer[`answer_${optionIndex}_selected`] === true) || 
           (Array.isArray(this.answer.selected_options) && this.answer.selected_options.includes(optionIndex)))) {
        console.log(`Option ${optionIndex} als KORREKT markiert durch Backend-Übereinstimmung`);
        return true;
      }
      
      return isCorrect;
    },
    handleOptionChange(event, optionIndex) {
      if (this.isReviewMode) return; // Im Review-Modus keine Änderungen erlauben
      
      // Prüfen, ob die aktuelle Frage mehrere Antworten erlaubt
      const allowMultiple = this.question.allow_multiple_answers === true;
      
      if (allowMultiple) {
        // Bei Multi-Choice: Option hinzufügen oder entfernen
        const index = this.selectedOptions.indexOf(optionIndex);
        
        if (event.target.checked && index === -1) {
          // Option hinzufügen
          this.selectedOptions.push(optionIndex);
        } else if (!event.target.checked && index !== -1) {
          // Option entfernen
          this.selectedOptions.splice(index, 1);
        }
      } else {
        // Bei Single-Choice: Alle vorherigen Optionen leeren und nur diese wählen
        this.selectedOptions = event.target.checked ? [optionIndex] : [];
      }
      
      // Die alte Emit-Event
      this.$emit('answer-changed', this.selectedOptions);
      // Neue Emit-Events für TestExecute.vue
      this.$emit('update:user-answers', this.selectedOptions);
    },
    handleTextInput() {
      if (this.isReviewMode) return; // Im Review-Modus keine Änderungen erlauben
      
      // Die alte Emit-Event
      this.$emit('answer-changed', this.textAnswer);
      // Neue Emit-Events für TestExecute.vue
      this.$emit('update:text-answer', this.textAnswer);
    }
  },
  mounted() {
    this.loadAnswer();
  }
};
</script>

<style lang="scss" scoped>
.question-display {
  width: 100%;
}

.question-view {
  .question-content {
    background-color: var(--card-bg, white);
    border-radius: 14px;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
    padding: 1.25rem;
    margin-bottom: 1rem;
    transition: all 0.3s ease;
    overflow: hidden;
    position: relative;
  }

  .question-media {
    margin: -1.25rem -1.25rem 1rem -1.25rem;
    height: 220px;
    overflow: hidden;
    border-bottom: 1px solid rgba(0,0,0,0.05);
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .question-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    
    .question-badge {
      font-size: 0.85rem;
      font-weight: 600;
      padding: 0.3rem 0.7rem;
      border-radius: 12px;
    }
  }

  .question-text {
    font-size: 1.05rem;
    margin-bottom: 1.25rem;
    color: var(--text-color, #111827);
    line-height: 1.5;
    
    .dark-mode & {
      color: var(--dark-text-color, #e2e8f0);
    }
  }
}

// Optionen für Multiple Choice
.answer-options {
  margin-bottom: 1rem;
  
  .option-item {
    display: flex;
    align-items: center;
    padding: 1rem 1.5rem;
    margin-bottom: 0.75rem;
    border: 2px solid var(--border-color);
    border-radius: 12px;
    background-color: var(--card-bg);
    transition: all 0.2s ease;
    cursor: pointer;
    position: relative;
    color: var(--text-color);

    &:hover {
      border-color: var(--primary-color-light);
      background-color: var(--hover-bg);
    }

    &.option-selected {
      border-color: var(--primary-color);
      background-color: rgba(var(--primary-color-rgb), 0.1);
      box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.2);


      .form-check-label {
        font-weight: 600;
        color: var(--primary-color);
      }
    }

    &.option-correct {
      border-color: var(--success-color);
      background-color: rgba(var(--success-color-rgb), 0.1);
    }

    &.option-incorrect {
      border-color: var(--danger-color);
      background-color: rgba(var(--danger-color-rgb), 0.1);
      opacity: 0.8;
    }

    .form-check {
      display: flex;
      align-items: center;
      width: 100%;
    }

    .form-check-input {
      width: 1.25rem;
      height: 1.25rem;
      margin-right: 0.75rem;
      flex-shrink: 0;
    }

    .form-check-label {
      flex-grow: 1;
      margin: 0;
      line-height: 1.5;
    }

    .option-icon {
      position: absolute;
      right: 1.5rem;
      top: 50%;
      transform: translateY(-50%);
      font-size: 1.25rem;

      .correct {
        color: var(--success-color);
      }

      .incorrect {
        color: var(--danger-color);
      }
    }
  }
}

// Text-Antwortfeld
.text-answer {
  textarea {
    width: 100%;
    border: 2px solid var(--border-color, #e5e7eb);
    border-radius: 12px;
    padding: 0.85rem;
    min-height: 120px;
    resize: vertical;
    transition: all 0.2s ease;
    font-size: 0.95rem;
    line-height: 1.5;
    
    &:focus {
      outline: none;
      border-color: var(--primary-color, #3b82f6);
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
    }
    
    .dark-mode & {
      background-color: var(--dark-bg-secondary, #1f2937);
      border-color: var(--dark-border-color, #374151);
      color: var(--dark-text-color, #e2e8f0);
      
      &:focus {
        border-color: var(--primary-color, #3b82f6);
      }
    }
  }
}
</style> 